import { useCallback, useLayoutEffect, useRef, useState } from "react";

export const useGroupedVirtualizedSelection = <Data>(
  controlledSelectedItems: readonly Data[] | undefined,
  onSelectedItemsChange: ((items: readonly Data[]) => void) | undefined,
  getItemKey: (item: Data) => string
) => {
  const [selectedItems, setSelectedItems] = useState<readonly Data[]>([]);
  const selectedSet = useRef(new Set<string>());

  const isSelected = useCallback(
    (item: Data) => {
      const key = getItemKey(item);
      return selectedSet.current.has(key);
    },
    [getItemKey]
  );

  const addItem = useCallback(
    (item: Data) => {
      const key = getItemKey(item);
      if (!selectedSet.current.has(key)) {
        selectedSet.current.add(key);
        setSelectedItems((selectedItems) => {
          const newSelectedItems = [...selectedItems, item];
          if (onSelectedItemsChange) {
            onSelectedItemsChange(newSelectedItems);
          }
          return newSelectedItems;
        });
      }
    },
    [onSelectedItemsChange, getItemKey]
  );

  const addItems = useCallback(
    (items: readonly Data[]) => {
      setSelectedItems((selectedItems) => {
        const newSelectedItems = [...selectedItems];
        for (const item of items) {
          const key = getItemKey(item);
          if (!selectedSet.current.has(key)) {
            selectedSet.current.add(key);
            newSelectedItems.push(item);
          }
        }
        if (onSelectedItemsChange) {
          onSelectedItemsChange(newSelectedItems);
        }
        return newSelectedItems;
      });
    },
    [onSelectedItemsChange, getItemKey]
  );

  const deleteItem = useCallback(
    (item: Data) => {
      const key = getItemKey(item);
      if (selectedSet.current.delete(key)) {
        setSelectedItems((selectedItems) => {
          const newSelectedItems = selectedItems.filter((item) => getItemKey(item) !== key);
          if (onSelectedItemsChange) {
            onSelectedItemsChange(newSelectedItems);
          }
          return newSelectedItems;
        });
      }
    },
    [onSelectedItemsChange, getItemKey]
  );

  const deleteItems = useCallback(
    (items: readonly Data[]) => {
      setSelectedItems((selectedItems) => {
        const deletedKeys = new Set<string>();
        for (const item of items) {
          const key = getItemKey(item);
          if (selectedSet.current.delete(key)) {
            deletedKeys.add(key);
          }
        }
        const newSelectedItems = selectedItems.filter((item) => !deletedKeys.has(getItemKey(item)));
        if (onSelectedItemsChange) {
          onSelectedItemsChange(newSelectedItems);
        }
        return newSelectedItems;
      });
    },
    [onSelectedItemsChange, getItemKey]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (controlledSelectedItems) {
      setSelectedItems((selectedItems) => {
        if (selectedItems === controlledSelectedItems) {
          return selectedItems;
        }
        selectedSet.current = controlledSelectedItems?.reduce(
          (newSelectedSet, item) => newSelectedSet.add(getItemKey(item)),
          new Set<string>()
        );
        return controlledSelectedItems;
      });
    }
  }, [controlledSelectedItems]);

  return { selectedItems, isSelected, addItem, addItems, deleteItem, deleteItems };
};
